import { useState, useEffect, useRef, useCallback } from 'react'
import { useTranslation } from 'react-i18next'
import { router } from '@inertiajs/react'
import { Button } from '@/components/ui/button'
import { RefreshCwIcon } from 'lucide-react'
import { ProjectsLayout } from '@/layouts/projects-layout'
import { SummaryStatsRow } from '@/components/production/SummaryStatsRow'
import { PlatformHealthSection } from '@/components/production/PlatformHealthSection'
import { AppMetricCard } from '@/components/production/AppMetricCard'
import { EmptyDashboard } from '@/components/production/EmptyDashboard'
import { csrfHeaders } from '@/lib/csrf'
import type { DeployedProject, Summary, RequestsData, DeploymentHealth } from '@/components/production/types'

interface ProductionDashboardProps {
  deployed_projects: DeployedProject[]
  summary: Summary
  deployment_health: DeploymentHealth
}

function ProductionDashboard({ deployed_projects, summary, deployment_health }: ProductionDashboardProps) {
  const { t } = useTranslation('projects')
  const [refreshKey, setRefreshKey] = useState(0)
  const [refreshing, setRefreshing] = useState(false)
  const [uptimeData, setUptimeData] = useState<Record<number, number>>({})
  const [requestsData, setRequestsData] = useState<RequestsData | null>(null)
  const asyncFetchedRef = useRef(false)

  const fetchAsyncMetrics = useCallback(async () => {
    try {
      const res = await fetch('/api/production/uptime_summary', { headers: csrfHeaders() })
      if (res.ok) {
        const data = await res.json()
        setUptimeData(data.uptime)
        setRequestsData(data.requests)
      }
    } catch {
      // Silently fail — supplementary data
    }
  }, [])

  useEffect(() => {
    if (asyncFetchedRef.current || deployed_projects.length === 0) return
    asyncFetchedRef.current = true
    fetchAsyncMetrics()
  }, [fetchAsyncMetrics, deployed_projects.length])

  const hasIssues = summary.total_exceptions > 0

  const handleRefresh = () => {
    setRefreshing(true)
    setRefreshKey((k) => k + 1)
    fetchAsyncMetrics()
    router.reload({
      only: ['deployed_projects', 'summary', 'deployment_health'],
      onFinish: () => setRefreshing(false)
    })
  }

  return (
    <div className="flex flex-1 flex-col">
      <div className="@container/main flex flex-1 flex-col gap-2">
        <div className="flex flex-col gap-2 py-2">
          <div className="px-4 lg:px-6">
            <div className="flex items-center justify-between mb-6">
              <div>
                <h1 className="text-2xl font-bold tracking-tight">{t('production.title')}</h1>
                <p className={`text-sm ${hasIssues ? 'text-orange-600 dark:text-orange-400' : 'text-green-600 dark:text-green-400'}`}>
                  {hasIssues ? t('production.issuesDetected') : t('production.allOperational')}
                </p>
              </div>
              <div className="flex items-center gap-2">
                <Button variant="outline" size="sm" onClick={handleRefresh} disabled={refreshing}>
                  <RefreshCwIcon className={`h-3.5 w-3.5 mr-1.5 ${refreshing ? 'animate-spin' : ''}`} />
                  {t('production.refresh')}
                </Button>
              </div>
            </div>

            {deployed_projects.length === 0 ? (
              <EmptyDashboard />
            ) : (
              <>
                <div className="mb-6">
                  <SummaryStatsRow summary={summary} requestsData={requestsData} />
                </div>

                <PlatformHealthSection key={refreshKey} deploymentHealth={deployment_health} />

                <div className="mb-6">
                  <h2 className="text-lg font-semibold mb-4">{t('production.apps.title')}</h2>
                  <div className="space-y-3">
                    {deployed_projects.map((project) => (
                      <AppMetricCard key={project.id} project={project} uptimePct={uptimeData[project.id]} />
                    ))}
                  </div>
                </div>
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  )
}

ProductionDashboard.layout = (page: React.ReactNode) => <ProjectsLayout>{page}</ProjectsLayout>

export default ProductionDashboard
